import {
  Button,
  Table,
  TableColumns,
  TableColumnWidth,
  TableData,
  TablePreferences,
  Select,
  Card,
  TextBox,
  IconWarningAlt,
  SelectOption,
  Modal,
  IconTrash,
  Checkbox,
  RadioButton,
  IconXCircle,
  IconCheckCircle,
  IconPencil,
  ListItem,
  OptionInterface,
  IconPlus,
  Tabs,
  ContextualMenu,
  IconSave,
  ButtonGroup,
  IconInfo,
} from "@veneer/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import "../../assets/css/views/surveys.css";
import surveyService from "../../services/surveyApi/survey.service";
import {
  ISurveyDetailDto,
  ISurveyDto,
} from "../../models/dto/surveyApi/ISurveyDto";
import SurveyEditorModal from "../../components/Surveys/SurveyEditorModal";
import SimpleMessageModalWithOpenClose from "../../components/Modals/SimpleMessageModalWithOpenClose";

interface IGroupedQuestion {
  questionID: number;
  question: string;
  options: string[];
  type: number;
}

export default function Surveys() {
  const [t] = useTranslation("global");
  type SelectValue = number | string | boolean; //CHECK

  const [loadingTableData, setLoadingTableData] = useState<boolean>(true);

  const [disabledFunctions, setDisableFunctions] = useState<boolean>(true);

  const [tableData, setTableData] = useState<TableData[]>([]);
  const [tableDataFiltered, setTableDataFiltered] = useState<TableData[]>([]);
  const [surveyIdSelected, setSurveyIdSelected] = useState<number>(NaN);
  let dataSurveys: ISurveyDto[] = [];

  const [search, setSearch] = useState<string>("");
  const [surveyDetailSelected, setSurveyDetailSelected] = useState<
    IGroupedQuestion[]
  >([]);

  //shows
  const [showModalConfirmDeletion, setShowModalConfirmDeletion] =
    useState<boolean>(false);
  const [showModalDetails, setShowModalDetails] = useState<boolean>(false);
  const [showModalEditor, setShowModalEditor] = useState<boolean>(false);

  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [surveyToEdit, setSurveyToEdit] = useState<ISurveyDto | undefined>(
    undefined
  );

  interface IModalMessage {
    show: boolean,
    message: string,
    title: string,
    onClose: React.Dispatch<React.SetStateAction<boolean>>
  };
  
  const [modalMessage, setModalMessage] = useState<IModalMessage>({
    show: false,
    message: '',
    title: '',
    onClose: function() {}
  });

  useEffect(() => {
    getSurveysData();
  }, []);

  const getSurveysData = () => {
    setLoadingTableData(true);
    try {
      surveyService.getSurveys().then((r) => {
        fillDataSurveys(r);
        dataSurveys = r;
      });
    } catch (error) {
      console.error(error);
    }
  };

  const fillDataSurveys = (data: ISurveyDto[]) => {
    const pagedData: TableData[] = [];
    for (let i = 0; i < data.length; i++) {
      const survey: ISurveyDto = data[i];
      let tableData: TableData = {};
      tableData = {
        surveyID: survey.surveyID,
        surveyName: survey.surveyName,
        startDate: formatDate(survey.startDate),
        endDate: formatDate(survey.endDate),
        dateCreated: formatDate(survey.dateCreated),
        isUndefined: survey.isUndefined ? <IconCheckCircle /> : <IconXCircle />,
        createdBy: survey.createdBy,
        rowConfig: {
          action: (
            <ContextualMenu
              onClick={handleActionRowConfig}
              options={[
                {
                  value: survey.surveyID,
                  label: t("header.details"),
                  icon: <IconInfo />,
                },
                {
                  value: survey.surveyID,
                  label: t("header.edit"),
                  icon: <IconPencil />,
                },
                {
                  value: survey.surveyID,
                  label: t("header.delete"),
                  icon: <IconTrash />,
                },
              ]}
              placement="leading"
            />
          ),
          disabled: false,
          noWrap: false,
          selected: false,
        },
      };
      pagedData.push(tableData);
    }
    setTableData(pagedData);
    setTableDataFiltered(pagedData);
    setLoadingTableData(false);
    setDisableFunctions(false);
  };

  const getSurveyDetails = (surveyId: number) => {
    try {
      surveyService
        .getSurveyDetail(surveyId)
        .then((r) => {
          fillDataSurveyDetails(r);
        })
        .then(() => setShowModalDetails(true));
    } catch (error) {
      console.error(error);
    }
  };

  const fillDataSurveyDetails = (surveyDetails: ISurveyDetailDto[]) => {
    let questions = JSON.parse(surveyDetails[0].questions);

    let questionsTemp: IGroupedQuestion[] = questions.map(
      (item: any, index: number) => {
        return {
          questionID: item.QuestionID,
          question: item.Question,
          options:
            item.Options.length > 0
              ? item.Options.map((option: any) => option.Option)
              : [],
          type: item.Type,
        };
      }
    );

    setSurveyDetailSelected(questionsTemp);
  };

  function formatDate(date: string) {
    var d = new Date(date),
      month = "" + (d.getMonth() + 1),
      day = "" + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  }

  const columns: TableColumns[] = [
    {
      id: "surveyName",
      label: t("header.survey"),
    },
    {
      id: "startDate",
      label: t("header.stardDate"),
    },
    {
      id: "endDate",
      label: t("header.endDate"),
    },
    {
      id: "dateCreated",
      label: t("header.createdDate"),
    },
    {
      id: "isUndefined",
      label: t("header.undefined"),
    },
    {
      id: "createdBy",
      label: t("header.createdBy"),
    },
  ];

  const columnsWidth: TableColumnWidth[] = [
    {
      columnId: "surveyName",
      width: "25%",
    },
    {
      columnId: "startDate",
      width: "15%",
    },
    {
      columnId: "endDate",
      width: "15%",
    },
    {
      columnId: "dateCreated",
      width: "15%",
    },
    {
      columnId: "isUndefined",
      width: "10%",
    },
    {
      columnId: "createdBy",
      width: "20%",
    },
  ];

  function getLabelQuestion(id: number): string | undefined {
    const QuestionTypes: { [key: number]: string } = {
      1: t("header.openQuestion"),
      2: t("header.multipleAnswer"),
      3: t("header.singleAnswer"),
      4: t("header.ratingQuestion"),
    };

    return QuestionTypes[id];
  }

  const tablePreferences: TablePreferences = {
    width: columnsWidth,
  };

  const handleChangeSearch = (value: string) => {
    setSearch(value);
    const filterArray = tableData.filter((element) => {
      if (
        element.surveyName
          .toString()
          .toLowerCase()
          .includes(String(value).toLowerCase())
      ) {
        return element;
      }
    });
    setTableDataFiltered(filterArray);
  };

  const handleActionRowConfig = (
    e: React.SyntheticEvent,
    option?: OptionInterface
  ) => {
    if (option) {
      if (
        option.label === "Details" ||
        option.label === "Detalles" ||
        option.label === "Detalhes"
      ) {
        setSurveyIdSelected(Number(option.value));
        getSurveyDetails(Number(option.value));
      } else if (
        option.label === "Edit" ||
        option.label === "Editar" ||
        option.label === "Editar"
      ) {
        setIsUpdating(true);
        const surveyTemp = dataSurveys.find(
          (item) => item.surveyID === Number(option.value)
        );
        if (surveyTemp) {
          setSurveyToEdit(surveyTemp);
          setShowModalEditor(true);
        }
      } else if (
        option.label === "Delete" ||
        option.label === "Eliminar" ||
        option.label === "Excluir"
      ) {
        setSurveyIdSelected(Number(option.value));
        setShowModalConfirmDeletion(true);
      }
    }
  };

  const confirmDeletion = () => {
    surveyService.deleteSurvey(surveyIdSelected).then((r) => {

      if ((r.statusCode && r.statusCode >= 400) || r === 0) {
        setModalMessage({
          show: true,
          message: t("modal.generalError"),
          title: t("header.error"),
          onClose: () => setModalMessage((prevState) => ({ ...prevState, show: false }))
        });    
      } else {
        setModalMessage({
          show: true,
          message: t("modal.surveyDeletedMessage"),
          title: t("modal.surveyDeleted"),
          onClose: () => {
            setModalMessage((prevState) => ({ ...prevState, show: false }));
            setLoadingTableData(true);
            setSearch("");
            getSurveysData();
            setShowModalConfirmDeletion(false);
            setSurveyIdSelected(NaN);
          }
        });
      }
    });
  };
  const cancelDeletion = () => {
    setSurveyIdSelected(NaN);
    setShowModalConfirmDeletion(false);
  };

  const onCloseEditorModal = () => {
    setIsUpdating(false);
    setSurveyToEdit(undefined);
    setShowModalEditor(false);
    getSurveysData();
  };

  return (
    <div className="surveys">
      <h4 className="surveysTittle">{t("header.surveys")}</h4>

      <Card
        appearance="outlined"
        className="surveysDiv"
        content={
          <>
            <div className="surveysDivTop">
              <TextBox
                className="searchSurveysTextbox"
                id="searchSurveysTextbox"
                label={t("header.search")}
                type="search"
                onChange={handleChangeSearch}
                value={search}
                disabled={disabledFunctions}
              />

              <Button onClick={() => setShowModalEditor(true)}>
                {t("header.createSurvey")}
              </Button>
            </div>

            <Table
              className="surveysTable"
              behavior={{
                type: "singlePage",
              }}
              columnResize
              preferences={tablePreferences}
              loading={loadingTableData}
              columns={columns}
              data={tableDataFiltered}
              loadingDataLength={10}
              customNoItems={
                <div className="customNoItems">
                  <div className="DivIconCustomNoItems">
                    <IconWarningAlt className="IconCustomNoItems" />
                  </div>
                  {t("header.noItemsFound")}
                </div>
              }
            />
          </>
        }
      />

      <div style={{ position: "relative" }}>
        <Modal
          align="center"
          id="modalSurveyDetails"
          show={showModalDetails}
          closeButton
          onClose={() => setShowModalDetails(false)}
          title={t("header.survey")}
        >
          <div className="modalSurveyDetailsList">
            <h6>{t("header.questions")}</h6>
            {surveyDetailSelected.length > 0 &&
              surveyDetailSelected.map(
                (survey: IGroupedQuestion, index: number) => {
                  return (
                    <ListItem
                      key={"survey" + index}
                      leadingArea={
                        <div>
                          <b>{survey.question!}</b>
                          <br></br>
                          <span style={{ fontSize: "small" }}>
                            {t("header.questionType") +
                              " " +
                              getLabelQuestion(survey.type)}{" "}
                          </span>
                          <div className="modalSurveyDetailsListOptions">
                            {survey.options?.map(
                              (option: string, indexOption: number) => {
                                if (survey.type === 2) {
                                  return (
                                    <Checkbox
                                      disabled
                                      id="checkbox"
                                      label={option}
                                    />
                                  );
                                } else if (survey.type === 3) {
                                  return (
                                    <RadioButton
                                      disabled
                                      id="radio-id"
                                      label={option}
                                    />
                                  );
                                }
                              }
                            )}
                          </div>
                        </div>
                      }
                    />
                  );
                }
              )}
          </div>
        </Modal>
      </div>

      <div style={{ position: "relative" }}>
        <Modal
          className="modalSurveyConfirmDeletion"
          align="center"
          id="modalSurveyConfirmDeletion"
          show={showModalConfirmDeletion}
          footer={
            <ButtonGroup className="modalSurveysConfirmDeletionButtonGroup">
              <Button onClick={() => confirmDeletion()}>
                {t("header.confirm")}
              </Button>
              <Button onClick={() => cancelDeletion()} appearance="secondary">
                {t("header.cancel")}
              </Button>
            </ButtonGroup>
          }
          title={t("header.deleteSurvey")}
        >
          <IconTrash size={120} />
          <br />
          <b>{t("header.messageDeleteSurvey")}</b>
        </Modal>
      </div>

      {showModalEditor && (
        <SurveyEditorModal
          onClose={onCloseEditorModal}
          isUpdating={isUpdating}
          surveyToEdit={surveyToEdit}
        />
      )}

      <SimpleMessageModalWithOpenClose
        showModal={modalMessage.show}
        setOpenCloseModal={modalMessage.onClose}
        modalTitle={modalMessage.title}
        modalMessage={modalMessage.message}
      />
    </div>
  );
}
